// Imports for external libraries go here.
import React, { FC } from 'react';
import clsx from 'clsx';
import { BrandRibbonProps } from './BrandRibbon.types';
import { StyledBrandRibbon } from './BrandRibbon.styles';
import { Link } from '@marriott/mi-ui-library';
import { MLogoContainer } from '@marriott/mi-web-ui-kit';
import { DARK_COLOR_SCHEME } from '../../modules/constants/constant';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useDCADynamicModelAlloy } from '../../modules/helpers';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

const BrandRibbon: FC<BrandRibbonProps> = props => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const model: BrandRibbonProps = targetData ? targetData : props;
  /**
   * Applying the logo color based on the color schemes
   */
  const darkColorSchemes = DARK_COLOR_SCHEME.split('|');
  const styleclasses = model?.styleclass.split(' ');
  const isDarkColorTheme = styleclasses.some(word => darkColorSchemes.includes(word));

  const isLogoFlagEnabled = props.featureFlagLogo?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION];

  const calculateBrandWidth = (brandName: string) => {
    let width = 60;
    let height = 30;
    switch (brandName) {
      case 'brand-logo-EB':
        width = 60.8;
        height = 42;
        break;
      case 'wh-secondary':
        width = 63;
        height = 42;
        break;
      case 'xr-secondary':
        width = 50.4;
        height = 42;
        break;
      case 'brand-logo-LC':
        width = 63;
        height = 42;
        break;
      case 'brand-logo-RZ':
        width = 63;
        height = 42;
        break;
      case 'brand-logo-JW':
        width = 63;
        height = 42;
        break;
    }
    return {
      svgWidth: width,
      svgHeight: height,
    };
  };

  return (
    <StyledBrandRibbon
      data-testid="luxury-BrandRibbon"
      data-component-name="m-luxury-BrandRibbon"
      className={clsx(model?.styleclass)}
      isLogoFlagEnabled={isLogoFlagEnabled}
    >
      <div
        className={clsx(
          'row d-flex flex-wrap justify-content-around align-items-center p-4 px-lg-3',
          'brand-ribbon-container'
        )}
      >
        <div
          className={clsx(
            'main-logo-container col-12 col-md-4 col-lg-3 p-xl-0 d-flex justify-content-around justify-content-lg-start',
            isDarkColorTheme ? 'dark-color-theme' : 'light-color-theme'
          )}
        >
          {!isLogoFlagEnabled ? (
            <Link
              linkHref={model?.mainLogoPath}
              custom_click_track_value={model?.clickTrackValue}
              linkClassName={'logo-link custom_click_track'}
              ariaLabel={model?.ariaLabel}
            >
              <span
                className={clsx('main-logo icon-class custom_click_track', model?.mainLogo)}
                custom_click_track_value={model?.clickTrackValue}
              />
              <span className="sr-only">{model?.location}</span>
            </Link>
          ) : (
            <MLogoContainer
              brand={model.mainLogo || ''}
              color={'background-aware'}
              aspectRatio={'4:2'}
              width={160}
              height={80}
              alignment={'Centered'}
              isLink={true}
              linkUrl={model.mainLogoPath}
            />
          )}
          <div className="t-horizontal-vertical-rule line-seperator"></div>
        </div>
        <div
          className={clsx(
            'brand-container pt-3 pt-md-0 col-12 col-md-8 col-lg-9 d-flex flex-wrap align-items-center',
            isDarkColorTheme ? 'dark-color-theme' : 'light-color-theme'
          )}
        >
          {!isLogoFlagEnabled
            ? model?.logoIcons?.map((list, index) => (
                <Link
                  linkHref={list?.linkUrl}
                  rel={list?.openInANewTab ? 'noopener noreferrer' : ''}
                  target={list?.openInANewTab ? '_blank' : '_self'}
                  custom_click_track_value={list?.clickTrackValue}
                  linkClassName={clsx(index === 0 && 'first-logo', 'logo-link custom_click_track py-2')}
                >
                  <span
                    className={clsx('icon-class custom_click_track', list?.icon)}
                    custom_click_track_value={list?.clickTrackValue}
                  />
                  <span className="sr-only">{list?.linkText}</span>
                </Link>
              ))
            : model?.logoIcons?.map(list => (
                <MLogoContainer
                  brand={list.icon || ''}
                  color={'background-aware'}
                  aspectRatio={'3:2'}
                  width={calculateBrandWidth(list.icon || '').svgWidth}
                  height={calculateBrandWidth(list.icon || '').svgHeight}
                  alignment={'Centered'}
                  isLink={true}
                  linkUrl={list.linkUrl}
                />
              ))}
        </div>
      </div>
    </StyledBrandRibbon>
  );
};

export const BrandRibbonConfigs = {
  emptyLabel: 'BrandRibbon',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/brand-ribbon/v1/brand-ribbon`,
};

export const BrandRibbonEditable = (props: BrandRibbonProps) => (
  <div data-testid="brand-ribbon" data-component-name="m-brand-ribbon">
    <EditableComponent config={BrandRibbonConfigs} {...props}>
      <BrandRibbon {...props} />
    </EditableComponent>
  </div>
);
